<template>
  <div class="tw:flex tw:items-center tw:gap-2">
    <input :id="id" :data-testid="dataTestId || id" :name="name" :parent="props.parent" :data-group="dataGroup" :data-checked-value="checkedValue" :data-parent="props.parent" type="checkbox" :checked="isChecked" :disabled="disabled" @input="handleInput" />
    <slot><TextPlay tag="label" color="var(--fontcolor)" weight="semibold" :for="id" :text="label" /></slot>
  </div>
</template>

<script lang="ts" setup>
import { ref, watch, computed } from 'vue';
import TextPlay from '../TextPlay/TextPlay.vue';

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | number | boolean | Array<string | number | boolean | object>): void;
  (e: 'input', value: string | number | boolean | Array<string | number | boolean | object>): void;
}>();

const props = withDefaults(defineProps<{
  parent?: boolean;
  dataGroup?: string | undefined;
  value?: string | number | boolean | Array<string | number | boolean | object>;
  modelValue?: string | number | boolean | Array<string | number | boolean | object>;
  checkedValue?: string | number | boolean;
  uncheckedValue?: string | number | boolean;
  id: string;
  name: string;
  label?: string;
  dataTestId?: string;
  disabled?: boolean;
}>(), {
  parent: false,
  dataGroup: undefined,
  value: undefined,
  modelValue: undefined,
  checkedValue: true,
  uncheckedValue: false,
  label: '',
  disabled: false,
  dataTestId: undefined,
});

const isChecked = ref(false);

const computedModel = computed(() => {
  return props.modelValue || props.value;
});

watch(computedModel, (newVal) => {
  if(Array.isArray(newVal)) {
    isChecked.value = newVal.includes(props.checkedValue!);
  } else {
    isChecked.value = newVal === props.checkedValue;
  }
}, { immediate: true });

function handleInput(event: Event) {
  const target = event.target as HTMLInputElement;
  let inputValue: string | number | boolean | Array<string | number | boolean | object>;

  if(Array.isArray(computedModel.value)) {
    if (target.checked) {
      // se for parent, adicionar lógica para adicionar todos os valores do grupo, menos o próprio
      if (props.parent) {
        inputValue = [...computedModel.value];
        // pega os checkbox que nao tem a propriedade "checked" do grupo, exceto o próprio
        const checkboxes = document.querySelectorAll(`input[type="checkbox"][data-group="${props.dataGroup}"]:not([data-parent="true"]):not([checked])`);
        checkboxes.forEach((checkbox) => {
          if (checkbox !== target) {
            inputValue.push(checkbox.getAttribute('data-checked-value')!);
          }
        });
      } else {
        inputValue = [...computedModel.value, props.checkedValue!];

        // verifica se todos os checkbox estao marcados, se sim, marca o checkbox parent
        const parentInput = document.querySelector(`input[type="checkbox"][data-group="${props.dataGroup}"][data-parent="true"]`) as HTMLInputElement;
        const groupCheckboxes = document.querySelectorAll(`input[type="checkbox"][data-group="${props.dataGroup}"]:not([data-parent="true"])`);
        let allChecked = true;
        groupCheckboxes.forEach((checkbox) => {
          if (!(checkbox as HTMLInputElement).checked) {
            allChecked = false;
          }
        });
        if (allChecked && parentInput) {
          parentInput.checked = true;
        }
      }
    } else {
      inputValue = computedModel.value.filter(item => item !== props.checkedValue);
      // se for parent, adicionar lógica para adicionar todos os valores do grupo
      if (props.parent) {
        inputValue = []        
      } else {
        const parentInput = document.querySelector(`input[type="checkbox"][data-group="${props.dataGroup}"][data-parent="true"]`) as HTMLInputElement;
        if (parentInput && parentInput.checked) {
          parentInput.checked = false;
        }
      }
    }
  } else {
    inputValue = target.checked ? props.checkedValue : props.uncheckedValue;
  }

  emit('update:modelValue', inputValue);
  emit('input', inputValue);
}
</script>

<style scoped>
input[type="checkbox"] {
  appearance: none;
  -webkit-appearance: none;
  width: 1rem;
  height: 1rem;
  border: 1.5px solid color-mix(in srgb, var(--fontcolor) 40%, transparent);
  border-radius: 3px;
  cursor: pointer;
  background-color: transparent;
  transition: background-color 0.15s, border-color 0.15s;
  flex-shrink: 0;
}

input[type="checkbox"]:checked {
  background-color: var(--maincolor);
  border-color: var(--maincolor);
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath fill='white' d='M13.707 4.293a1 1 0 0 1 0 1.414l-7 7a1 1 0 0 1-1.414 0l-3-3a1 1 0 0 1 1.414-1.414L6 10.586l6.293-6.293a1 1 0 0 1 1.414 0z'/%3E%3C/svg%3E");
  background-size: 12px 12px;
  background-repeat: no-repeat;
  background-position: center;
}

input[type="checkbox"]:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>